import {ActionList} from '@primer/react'
import React, {useCallback, useRef} from 'react'
import {useVirtualizer, type VirtualItem, type Virtualizer} from '@tanstack/react-virtual'

export interface FixedSizeVirtualizedListProps<T> {
  items: T[]
  itemHeight: number
  renderItem: (item: T, index: number) => React.ReactNode
  makeKey: (item: T) => string
  style?: React.CSSProperties // style to apply to the list container
  ariaControls?: string
  shouldUseActionList?: boolean // determines if ActionList or ul is used to wrap list items
  className?: string
  containerRole?: string
}

export function FixedSizeVirtualList<T>({
  items,
  itemHeight,
  style,
  renderItem,
  makeKey,
  ariaControls,
  shouldUseActionList = false,
  className,
  containerRole = 'none',
}: FixedSizeVirtualizedListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: useCallback(() => parentRef.current, []),
    estimateSize: useCallback(() => itemHeight, [itemHeight]),
  })

  return (
    <ListContainer
      ref={parentRef}
      style={style}
      virtualizer={virtualizer}
      id={ariaControls}
      shouldUseActionList={shouldUseActionList}
      className={className}
    >
      {virtualizer.getVirtualItems().map((virtualRow, index) => (
        <ItemContainer containerRole={containerRole} key={makeKey(items[virtualRow.index]!)} virtualRow={virtualRow}>
          {renderItem(items[virtualRow.index]!, index)}
        </ItemContainer>
      ))}
    </ListContainer>
  )
}

function ListContainer({
  ref: forwardedRef,
  children,
  style,
  virtualizer,
  id,
  shouldUseActionList,
  className,
}: React.PropsWithChildren<{
  virtualizer: Virtualizer<HTMLDivElement, Element>
  style?: React.CSSProperties
  id?: string
  shouldUseActionList?: boolean
  className?: string
  ref?: React.Ref<HTMLDivElement>
}>) {
  return (
    <div ref={forwardedRef} style={style} id={id} className={className}>
      {shouldUseActionList ? (
        <ActionList
          id={id}
          role="menu"
          style={{height: virtualizer.getTotalSize(), width: '100%', position: 'relative'}}
          selectionVariant={'single'}
        >
          {children}
        </ActionList>
      ) : (
        <ul role="menu" style={{height: virtualizer.getTotalSize(), width: '100%', position: 'relative'}} id={id}>
          {children}
        </ul>
      )}
    </div>
  )
}

function ItemContainer({
  children,
  containerRole = 'none',
  virtualRow,
}: React.PropsWithChildren<{containerRole?: string; virtualRow: VirtualItem}>) {
  // Note: all of these styles are necessary. Each item must be
  // absolutely positioned and moved around with a css transform or
  // else the list virtualization will not work.
  return (
    <li
      role={containerRole}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      {children}
    </li>
  )
}
